import React, { useState, useEffect, memo, useCallback } from 'react';
import { format } from 'date-fns';

const PgcNotifTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const fetchPgcData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/pgc/getMissingPgcs');
      // const response = await fetch('/api/pgc/getMissingPgcss');

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      if (Array.isArray(result)) {
        const filteredResult = result
          .map(item => ({
            ...item,
            operation: item.operation.charAt(0).toUpperCase() + item.operation.slice(1).toLowerCase(),
            sci_sccv: item.sci_sccv.toUpperCase(),
            ville_op: item.ville_op.toUpperCase()
          }));
        setData(filteredResult);
      } else setData([])
    } catch (err) {
      console.error('Erreur lors du chargement des données PGC:', err);
      setError(err.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPgcData();
  }, [fetchPgcData]);

  const calculateTooltipPosition = useCallback((mouseX, mouseY) => {
    const tooltipWidth = 250;
    const tooltipHeight = 130;
    const offset = 10;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = mouseX + offset;
    let y = mouseY + offset;

    if (x + tooltipWidth > viewportWidth) {
      x = mouseX - tooltipWidth - offset;
    }

    if (y + tooltipHeight > viewportHeight) {
      y = mouseY - tooltipHeight - offset;
    }

    if (x < 0) {
      x = offset;
    }

    if (y < 0) {
      y = offset;
    }

    return { x, y };
  }, []);

  const handleMouseMove = useCallback((e) => {
    const position = calculateTooltipPosition(e.clientX, e.clientY);
    setTooltipPosition(position);
  }, [calculateTooltipPosition]);

  const renderPgcItem = useCallback((pgc, index) => (
    <div
      key={pgc.id || index}
      className={`pgc-item ${hoveredRow === index ? 'hovered' : ''}`}
      onMouseEnter={() => setHoveredRow(index)}
      onMouseLeave={() => setHoveredRow(null)}
      onMouseMove={handleMouseMove}
    >
      <span className="pgc-dossier" style={!pgc.ndossier ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {pgc.ndossier || 'Indisponible'}
      </span>
      <span className="pgc-separator"> - </span>
      <span className="pgc-lot" style={!pgc.sci_sccv ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {pgc.sci_sccv || 'Indisponible'}
      </span>
      <span className="pgc-separator"> - </span>
      <span className="pgc-company" style={!pgc.ville_op ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {pgc.ville_op || 'Indisponible'}
      </span>
    </div>
  ), [hoveredRow, handleMouseMove]);

  if (loading) {
    return (
      <div className="pgc-notif-table loading">
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pgc-notif-table error">
        <div className="error-message">
          Erreur: {error}
        </div>
        <button
          onClick={fetchPgcData}
          className="retry-button"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="pgc-notif-table empty">
        <div className="empty-message">Aucun PGC manquant trouvé.</div>
      </div>
    );
  }

  return (
    <div className="pgc-notif-table">
      <div className="pgc-notif-header">
        <span>PGC à faire ({data.length})</span>
      </div>
      <div className="pgc-notif-list">
        {data.map(renderPgcItem)}
      </div>
      {hoveredRow !== null && (
        <div
          className="pgc-notif-tooltip"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            position: 'fixed'
          }}
        >
          <p><b>Dossier:</b> <span style={!data[hoveredRow].ndossier ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].ndossier || 'Indisponible'}</span></p>
          <p><b>Maître d'Ouvrage:</b> <span style={!data[hoveredRow].sci_sccv ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].sci_sccv || 'Indisponible'}</span></p>
          <p><b>Opération:</b> <span style={!data[hoveredRow].operation ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].operation || 'Indisponible'}</span></p>
          <p><b>Ville:</b> <span style={!data[hoveredRow].ville_op ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].ville_op || 'Indisponible'}</span></p>
          <p><b>Facture C1 soldée:</b> <span style={!data[hoveredRow].payment_date ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow]?.payment_date ? format(data[hoveredRow]?.payment_date, 'dd/MM/yyyy') : 'Indisponible'}</span></p>
        </div>
      )}
    </div>
  );
};

export default memo(PgcNotifTable);